/*
  Dabi Deri — sayfalama davranışları (snippets/dabi-pagination.liquid).

  - pages : bağlantıya tıklanınca yeni sayfa bölüm olarak indirilir,
            ürün alanı yerinde değişir, URL güncellenir (facets.js ile aynı
            geçmiş biçimi: { searchParams }). Liste başına yumuşak kaydırılır.
  - button: "Daha fazla göster" bir sonraki sayfanın kartlarını listenin
            altına ekler; sayfalayıcı yenisiyle değişir.
  - auto  : sayfalayıcı görünür alana girince kendiliğinden yükler; ayarla
            verilen sayfa sayısından sonra düğmeye döner.
  - Tümünü göster: kalan sayfaları sırayla ekler.

  Filtre ya da sıralama değişince Craft alanı yeniden basar; kurulum tek bir
  gözlemciyle her seferinde tekrarlanır. Eklenen kartlar için Craft'ın
  kaydırma animasyonu sınıfları kaldırılır, kendi giriş animasyonu kullanılır.
*/

(() => {
  const GRID = '#product-grid';
  const CONTAINER = '#ProductGridContainer';

  let busy = false;
  let observer = null;

  const stripReveal = (item) => {
    item.classList.remove('scroll-trigger', 'animate--slide-in');
    item.removeAttribute('data-cascade');
    item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.remove('scroll-trigger', 'animate--slide-in'));
  };

  const fetchSection = async (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const setStatus = (pager, shown, total) => {
    const status = pager.querySelector('[data-dabi-pager-status]');
    const bar = pager.querySelector('.dabi-pager__bar > span');
    if (status) {
      status.textContent = (pager.dataset.labelShowing || '')
        .replace('[count]', String(shown))
        .replace('[total]', String(total));
    }
    if (bar) bar.style.width = `${Math.min(100, (shown / Math.max(1, total)) * 100)}%`;
  };

  /* ---------- daha fazla / tümü ---------- */

  const appendNext = async (pager) => {
    const next = pager.dataset.next;
    const grid = document.querySelector(GRID);
    if (!next || !grid || busy) return false;
    busy = true;
    pager.classList.add('is-loading');
    const more = pager.querySelector('[data-dabi-pager-more]');
    if (more) more.disabled = true;

    try {
      const doc = await fetchSection(next, pager.dataset.section);
      const items = Array.from(doc.querySelectorAll(`${GRID} > li`));
      const start = grid.children.length;
      items.forEach((item, i) => {
        stripReveal(item);
        item.classList.add('dabi-pager__new');
        item.style.setProperty('--dabi-i', String(Math.min(i, 11)));
        grid.appendChild(item);
      });
      const fresh = doc.querySelector('[data-dabi-pager]');
      if (fresh) {
        // Yükleme sırasında biriken kalıcı durum (kip, sayaç) yeni öğede zaten var.
        fresh.dataset.loaded = String(Number(pager.dataset.loaded || 0) + 1);
        pager.replaceWith(fresh);
        setStatus(fresh, Number(fresh.dataset.shown), Number(fresh.dataset.total));
        setup(fresh);
        // Yeni kartlardan ilkine odak: klavye kullanıcısı listenin sonuna gelmiş oldu.
        const firstNew = grid.children[start]?.querySelector('a, button');
        if (firstNew && document.activeElement && pager.contains(document.activeElement)) firstNew.focus({ preventScroll: true });
        return !!fresh.dataset.next;
      }
      pager.remove();
      return false;
    } catch (err) {
      pager.classList.remove('is-loading');
      if (more) more.disabled = false;
      return false;
    } finally {
      busy = false;
    }
  };

  const appendAll = async (pager) => {
    let current = pager;
    let guard = 0;
    current.classList.add('is-loading-all');
    while (current && current.dataset.next && guard < 40) {
      guard += 1;
      // eslint-disable-next-line no-await-in-loop
      const hasMore = await appendNext(current);
      current = document.querySelector('[data-dabi-pager]');
      if (current) current.classList.add('is-loading-all');
      if (!hasMore) break;
    }
  };

  /* ---------- sayfa geçişi (pages) ---------- */

  const swapPage = async (pager, href, push = true) => {
    const container = document.querySelector(CONTAINER);
    if (!container || busy) return;
    busy = true;
    const collection = container.querySelector('.collection');
    collection?.classList.add('loading');
    container.classList.add('dabi-pager-swap');
    try {
      const doc = await fetchSection(href, pager.dataset.section);
      const incoming = doc.querySelector(CONTAINER);
      if (!incoming) throw new Error('no grid');
      const url = new URL(href, window.location.origin);
      if (push) history.pushState({ searchParams: url.search.slice(1) }, '', `${url.pathname}${url.search}`);
      // Craft'ın geri/ileri denetimi (facets.js) aynı geçmiş biçimini bekliyor
      // ve son bilinen sorguyla karşılaştırıyor; o değer güncel tutulur ki
      // geri tuşunda önceki sayfayı Craft yeniden bassın.
      const facetsClass = window.customElements.get('facet-filters-form');
      if (facetsClass) facetsClass.searchParamsPrev = url.search.slice(1);
      container.innerHTML = incoming.innerHTML;
      container.querySelectorAll(`${GRID} > li`).forEach((item, i) => {
        stripReveal(item);
        item.classList.add('dabi-pager__new');
        item.style.setProperty('--dabi-i', String(Math.min(i, 11)));
      });
      const top = (document.querySelector('.dabi-listing') || container).getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      container.querySelectorAll('[data-dabi-pager]').forEach(setup);
    } catch (err) {
      window.location.assign(href);
    } finally {
      busy = false;
      container.classList.remove('dabi-pager-swap');
    }
  };

  /* ---------- kurulum ---------- */

  const setup = (pager) => {
    if (!pager || pager.dataset.dabiReady === 'true') return;
    pager.dataset.dabiReady = 'true';

    pager.querySelector('[data-dabi-pager-more]')?.addEventListener('click', () => appendNext(pager));
    pager.querySelector('[data-dabi-pager-all]')?.addEventListener('click', () => appendAll(pager));

    if (pager.dataset.transition === 'true') {
      pager.querySelectorAll('[data-dabi-pager-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          swapPage(pager, link.getAttribute('href'));
        });
      });
    }

    if (pager.dataset.mode === 'auto' && 'IntersectionObserver' in window) {
      const loaded = Number(pager.dataset.loaded || 0);
      const limit = Number(pager.dataset.autoPages || 4);
      if (loaded < limit && pager.dataset.next) {
        if (observer) observer.disconnect();
        observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            observer.disconnect();
            appendNext(pager);
          },
          { rootMargin: '0px 0px 40% 0px' }
        );
        observer.observe(pager);
      } else {
        pager.classList.add('is-auto-paused');
      }
    }
  };

  const setupAll = () => document.querySelectorAll('[data-dabi-pager]').forEach(setup);

  const start = () => {
    setupAll();
    const container = document.querySelector(CONTAINER);
    if (container) new MutationObserver(setupAll).observe(container, { childList: true });
  };

  // Filtre bileşeni yoksa geri/ileri tuşunu burada karşıla.
  window.addEventListener('popstate', () => {
    if (window.customElements.get('facet-filters-form')) return;
    const pager = document.querySelector('[data-dabi-pager][data-transition="true"]');
    if (pager) swapPage(pager, window.location.href, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
